import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
  Fade,
  Chip,
  Stack
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  FileCopy as FileCopyIcon,
  CheckCircleOutline as CheckCircleIcon
} from '@mui/icons-material';
import { api } from '@services';
import { detectDocumentType, STANDARD_DOCUMENT_TYPES } from '@utils/documentTypeUtils';

// Define document types
const documentTypes = [
  { value: 'Financial Document', label: 'Financial Document' },
  { value: 'Profit & Loss Statement', label: 'Profit & Loss Statement' },
  { value: 'ADR Metrics', label: 'ADR Metrics' },
  { value: 'RevPAR Metrics', label: 'RevPAR Metrics' },
  { value: 'Occupancy Metrics', label: 'Occupancy Metrics' },
  { value: 'STR Report', label: 'STR Report' },
  { value: 'Miscellaneous Document', label: 'Other Document' }
];

interface UploadDocumentHandlerProps {
  hotelId: string;
  onSuccess?: (message: string, fileRecord?: any) => void;
  onError?: (message: string) => void;
}

const UploadDocumentHandler: React.FC<UploadDocumentHandlerProps> = ({
  hotelId,
  onSuccess,
  onError
}) => {
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Default to 'Financial Document' so AI Analysis always picks up uploads unless user chooses otherwise
const [documentType, setDocumentType] = useState('Financial Document');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ [fileName: string]: string }>({});

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
      setError(null);
      // Optionally, detect document type for the first file
      const detectedType = detectDocumentType(files[0].name);
      setDocumentType(detectedType || 'Financial Document');
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !documentType) {
      const errorMsg = 'Please select at least one file and document type.';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    setLoading(true);
    setError(null);
    setSuccess(null);
    const status: { [fileName: string]: string } = {};

    for (const file of selectedFiles) {
      if (file.size > MAX_FILE_SIZE) {
        status[file.name] = 'File size exceeds the 10MB limit';
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('hotelId', hotelId);
      formData.append('documentType', documentType);
      formData.append('document_type', documentType);
      formData.append('category', documentType);

      try {
        let response;
        try {
          // Prefer the unified uploads route (returns hotel_documents id)
          const uploadFormData = new FormData();
          uploadFormData.append('files', file);
          uploadFormData.append('hotelId', hotelId);
          uploadFormData.append('documentType', documentType);
          uploadFormData.append('document_type', documentType);
          uploadFormData.append('category', documentType);

          response = await api.post(`/uploads/hotels/${hotelId}/documents`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000,
          });
        } catch (primaryError) {
          // Fallback to /ocr/upload if the unified route is unavailable
          console.warn('Fallback to /ocr/upload', primaryError);
          response = await api.post('/ocr/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000,
          });
        }
        status[file.name] = 'Uploaded successfully';
        const fileRecord = response.data?.files?.[0] || response.data?.file || response.data;
        if (onSuccess) onSuccess(`${file.name} uploaded successfully as ${documentType}`, fileRecord);
      } catch (err: any) {
        const errorMsg = err.response?.data?.details || err.response?.data?.message || err.message || 'Error uploading file';
        status[file.name] = errorMsg;
        if (onError) onError(errorMsg);
      }
    }
    setUploadStatus(status);
    setLoading(false);
    setSelectedFiles([]);
    setDocumentType('');
    const fileInput = document.getElementById('document-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (file: File): string => {
    const bytes = file.size;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 25px rgba(0,0,0,0.08)',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2
      }}>
        <DescriptionIcon color="primary" fontSize="large" />
        <Typography variant="h6" fontWeight={600}>
          Upload New Document
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(211,47,47,0.1)'
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in={!!success}>
          <Alert
            severity="success"
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(46,125,50,0.1)'
            }}
            onClose={() => setSuccess(null)}
            icon={<CheckCircleIcon />}
          >
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel id="document-type-label">Document Type *</InputLabel>
          <Select
            labelId="document-type-label"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            label="Document Type *"
            disabled={loading}
          >
            <MenuItem value="" disabled>
              <em>Select document type</em>
            </MenuItem>
            {documentTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{
          p: 3,
          border: '2px dashed',
          borderColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 2,
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: theme.palette.primary.main
          }
        }}>
          <input
            id="document-file-input"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.csv,.xls"
            multiple
          />

          <label htmlFor="document-file-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
            {!selectedFiles.length ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5
              }}>
                <CloudUploadIcon
                  sx={{
                    fontSize: 50,
                    color: alpha(theme.palette.primary.main, 0.7)
                  }}
                />
                <Typography variant="body1" fontWeight={500}>
                  Drag & drop your file here or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: PDF, PNG, JPG, Excel (.xlsx), CSV (max 10MB)
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5
              }}>
                <FileCopyIcon
                  sx={{
                    fontSize: 40,
                    color: theme.palette.primary.main
                  }}
                />
                <Stack spacing={1} alignItems="center">
                  {selectedFiles.map((file) => (
                    <Box key={file.name} sx={{ mb: 1, width: '100%' }}>
                      <Typography variant="body1" fontWeight={600}>
                        {file.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={formatFileSize(file)}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500
                          }}
                        />
                        {documentType && (
                          <Chip
                            label={documentType}
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              fontWeight: 500
                            }}
                          />
                        )}
                        {uploadStatus[file.name] && (
                          <Chip
                            label={uploadStatus[file.name]}
                            size="small"
                            color={uploadStatus[file.name] === 'Uploaded successfully' ? 'success' : 'error'}
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                    Click to change file(s)
                  </Typography>
                </Stack>
              </Box>
            )}
          </label>
        </Box>
      </Box>

      <Button
        variant="contained"
        fullWidth
        onClick={handleUpload}
        disabled={loading || !selectedFiles.length || !documentType}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        sx={{
          py: 1.2,
          borderRadius: 2,
          fontWeight: 600,
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s',
          // Default colors when not loading
          backgroundColor: loading ? theme.palette.warning.main : theme.palette.primary.main,
          color: loading ? theme.palette.warning.contrastText : theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: loading ? theme.palette.warning.dark : theme.palette.primary.dark,
            boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)'
          }
        }}
      >
        {loading ? 'Processing Document...' : 'Upload Document'}
      </Button>
    </Paper>
  );
};

export default UploadDocumentHandler;